// @flow

import {Http} from './http';
import {providerUrl as _providerUrl, providerUrlV2 as _providerUrlV2, storageUrl, vodUrl} from './config';

/**
 * Normaliza las listas de CDN (`string` en la v1, `{url, tokenized}` en la v2)
 * al array de hosts planos que esperan playkit-js-hls y el overlay de error.
 * @param {any} list lista de CDNs
 * @returns {string[]} hosts
 */
function _hosts(list: any): string[] {
  if (!Array.isArray(list)) return [];
  return list.map(item => (typeof item === 'string' ? item : item && item.url)).filter(item => item && item.length);
}

/**
 * @param {any} playerConfig configuración del player
 * @param {number} version 1 usa providerUrl, 2 usa providerUrlV2
 * @returns {string} host de las listas de CDN
 */
function _base(playerConfig: any, version: number): string {
  const configured = version === 2 ? playerConfig.providerUrlV2 : playerConfig.providerUrl;
  if (configured !== undefined && configured !== null && configured) return configured;
  return version === 2 ? _providerUrlV2 : _providerUrl;
}

/**
 * URL del fichero de CDNs del usuario. La v1 vive en fileover y la v2 en /json/live.
 * @param {string} user usuario
 * @param {any} playerConfig configuración del player
 * @param {number} version versión del proveedor
 * @returns {string} url
 */
export function cdnListUrl(user: string, playerConfig: any, version: number): string {
  const base = _base(playerConfig, version);
  return version === 2 ? `${base}/json/live/${user}.json` : `${base}/definition/fileover/${user}.json`;
}

/**
 * @param {any} playerConfig configuración del player
 * @param {number} version versión del proveedor
 * @returns {string} url del fallback
 */
function _fallbackUrl(playerConfig: any, version: number): string {
  const base = _base(playerConfig, version);
  return version === 2 ? `${base}/json/live/default.json` : `${base}/definition/fileover/default.json`;
}

/**
 * defaultJSON
 * La v1 pide `/definition/fileover` en providerUrl. La v2 pide `/json/live` en providerUrlV2.
 * @param {string} user user
 * @param {any} playerConfig playerConfig
 * @param {string} stream stream
 * @param {number} version 1 o 2
 * @returns {Promise<any>} listas de CDN
 */
export default async function defaultJSON(user: string, playerConfig: any, stream?: string, version?: number): Promise<any> {
  const providerVersion = version === 2 ? 2 : 1;
  const vodUrls = playerConfig.vodUrl !== undefined && playerConfig.vodUrl ? playerConfig.vodUrl : vodUrl;
  const stirageUrls = playerConfig.storageUrl !== undefined && playerConfig.storageUrl ? playerConfig.storageUrl : storageUrl;
  let data = {
    urls: [],
    vod: vodUrls,
    storage: [stirageUrls]
  };

  let streamUrls = [];
  if (stream !== undefined && stream) {
    try {
      const dataUserJson = await Http.execute(cdnListUrl(user, playerConfig, providerVersion), null, 'GET');
      streamUrls = _hosts(dataUserJson[stream]);
    } catch (e) {
      // se resuelve con la lista de CDN por defecto
    }
  }
  if (streamUrls.length) {
    data.urls = streamUrls;
    return data;
  }

  const defaultDataJSON = await Http.execute(_fallbackUrl(playerConfig, providerVersion), null, 'GET');
  // La v1 anida las listas en `{default}` o en la clave del usuario. La v2 las trae en la raíz.
  const entry =
    providerVersion === 2
      ? defaultDataJSON
      : (defaultDataJSON[user] && defaultDataJSON[user].urls && defaultDataJSON[user]) || defaultDataJSON.default || {};
  data.urls = _hosts(entry.urls);
  if (playerConfig.vodUrl === undefined || !playerConfig.vodUrl) {
    const defaultVodUrls = _hosts(entry.vod);
    if (defaultVodUrls.length) data.vod = defaultVodUrls;
  }
  if ((playerConfig.storageUrl === undefined || !playerConfig.storageUrl) && _hosts(entry.storage).length) {
    data.storage = _hosts(entry.storage);
  }
  return data;
}
